// The deal's fields as a form, shared by the form that creates and edits a deal (DealForm.jsx) and
// by the deal's page, where with the deal flow on the current stage's fields are edited
// (DealStageForm.jsx). The caller holds the draft and saves it.
//
// With the flow off the state is one of the fields. With it on the stage is moved on the deal's
// page instead, the terms the flow adds are here (DealTermsFields.jsx), and `marks` says which
// fields the next move waits on and which an approved round locked (FieldMark.jsx).

import {
  DIRECTIONS,
  MAX_DEAL_NUMBER_LENGTH,
  MAX_INTERNAL_ID_LENGTH,
  MAX_TRADER_LENGTH,
  STATES,
  counterpartyLabel,
  dealValue,
  formatMoney,
} from './dealForm.js';
import DealTermsFields from './DealTermsFields.jsx';
import { FieldFlags, Locked } from './FieldMark.jsx';
import CounterpartySelect from './fields/CounterpartySelect.jsx';
import ListSelect from './fields/ListSelect.jsx';
import PriceInput from './fields/PriceInput.jsx';
import QuantityInput from './fields/QuantityInput.jsx';
import SegmentedChoice from './fields/SegmentedChoice.jsx';

export default function DealFieldset({
  draft, set, flow, creating = false, lists, vocabulary, fieldErrors, canAddEntries, marks,
}) {
  const counterpartyRole = counterpartyLabel(draft.direction);
  const value = dealValue(draft);
  const flags = (field) => <FieldFlags field={field} marks={marks} />;

  return (
    <>
      <label className="field">
        <span className="field-label">Deal number</span>
        {/* Left empty, the deal takes the number the system assigns it on save. A deal that
            predates T-REX is given the number it already carries. */}
        <input value={draft.dealNumber} onChange={(e) => set('dealNumber')(e.target.value)}
               maxLength={MAX_DEAL_NUMBER_LENGTH} autoComplete="off"
               placeholder="Assigned on save" />
        {creating && (
          <span className="hint">Leave empty for a new deal, or type an existing deal’s number.</span>
        )}
        {fieldErrors.deal_number && (
          <span className="field-error">{fieldErrors.deal_number}</span>
        )}
      </label>

      <label className="field">
        <span className="field-label">Internal id</span>
        <input value={draft.internalId} onChange={(e) => set('internalId')(e.target.value)}
               maxLength={MAX_INTERNAL_ID_LENGTH} autoComplete="off" />
        {fieldErrors.internal_id && (
          <span className="field-error">{fieldErrors.internal_id}</span>
        )}
      </label>

      {!flow && (
        <SegmentedChoice label="State" options={STATES} value={draft.state}
                         onChange={set('state')} error={fieldErrors.state} />
      )}

      <fieldset>
        <legend>Counterparty</legend>
        <Locked field="direction" marks={marks}>
          <SegmentedChoice label="Auremin" options={DIRECTIONS} value={draft.direction}
                           onChange={set('direction')} error={fieldErrors.direction}
                           flag={flags('direction')} />
        </Locked>
        <p className="hint">
          Pick the {counterpartyRole.toLowerCase()} from the register to link it to the deal. A
          name that is not on the register is kept as typed and marked as not linked.
        </p>
        <Locked field="counterparty" marks={marks}>
          <CounterpartySelect
            label={counterpartyRole}
            resolved={lists.counterparties.find((c) => c.counterparty_id === draft.counterpartyId)}
            name={draft.counterpartyName}
            linked={Boolean(draft.counterpartyId)}
            error={fieldErrors.counterparty}
            flag={flags('counterparty')}
            onChange={(name, chosen) => {
              set('counterpartyName')(name);
              // A record that was picked carries its own identifier. Typing over a selection sends
              // no record, so the name is matched against the list - and matching nothing makes it
              // free text again, rather than leaving an id attached to a different name.
              set('counterpartyId')(
                chosen ? chosen.counterparty_id : matchId(lists.counterparties, name),
              );
            }}
          />
        </Locked>
      </fieldset>

      <Locked field="product" marks={marks}>
        <ListSelect label="Product" listName="product" entries={lists.product}
                    value={draft.product} onChange={set('product')}
                    error={fieldErrors.product} onAdded={lists.refresh} canAdd={canAddEntries}
                    flag={flags('product')} />
      </Locked>

      <label className="field">
        <span className="field-label">Trader</span>
        <input value={draft.trader} onChange={(e) => set('trader')(e.target.value)}
               maxLength={MAX_TRADER_LENGTH} autoComplete="off" />
        {fieldErrors.trader && <span className="field-error">{fieldErrors.trader}</span>}
      </label>

      <Locked field="quantity" marks={marks}>
        <QuantityInput amount={draft.quantityAmount} unit={draft.quantityUnit}
                       units={lists.unit} onAmount={set('quantityAmount')}
                       onUnit={set('quantityUnit')} error={fieldErrors['quantity.unit']}
                       onAdded={lists.refresh} canAdd={canAddEntries}
                       flag={flags('quantity')} />
      </Locked>

      <Locked field="price" marks={marks}>
        <PriceInput amount={draft.priceAmount} currency={draft.priceCurrency}
                    currencies={lists.currency} unit={draft.quantityUnit}
                    onAmount={set('priceAmount')} onCurrency={set('priceCurrency')}
                    error={fieldErrors.price ?? fieldErrors['price.currency']}
                    onAdded={lists.refresh} canAdd={canAddEntries} flag={flags('price')} />
      </Locked>

      {/* Computed here as it is typed and again by the server, which never takes it from us. */}
      <p className="field deal-value">
        <span className="field-label">Deal value</span>
        <output aria-live="polite">
          {value ? formatMoney(value) : <span className="muted">Volume × price</span>}
        </output>
      </p>

      {flow && (
        <DealTermsFields draft={draft} set={set} lists={lists} vocabulary={vocabulary}
                         fieldErrors={fieldErrors} canAddEntries={canAddEntries} marks={marks} />
      )}

      <label className="field">
        <span className="field-label">Notes</span>
        <textarea value={draft.notes} onChange={(e) => set('notes')(e.target.value)} rows={3} />
      </label>
    </>
  );
}

// A typed name that exactly matches a counterparty resolves to it; anything else is free text.
function matchId(counterparties, name) {
  const match = counterparties.find((cp) => cp.name === name.trim());
  return match ? match.counterparty_id : null;
}
